"use client"
import styled from "@emotion/styled"
import { SwipeableDrawer } from "@mui/material"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React, { useEffect, useState } from "react"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import { transformProperties } from "utils/simplifyData"
import { colorPalletteIndexType } from "../common/bit/ButtonWithArrow"
import { Desktop, Mobile } from "../common/bit/MediaQueries"
import IconsLinksList, {
  ActiveItem,
  CardTitle,
  IconCard,
  IconWrapper,
} from "../IconsLinksList/IconsLinksList"
import { sizes } from "../theme/sizes"

interface IconItem {
  properties: {
    Icon: {
      mediaItems: Array<{
        url: string
      }>
    }
    text: {
      value: string
    }
    link: {
      links: Array<{
        url: string
      }>
    }
    adamTotalID?: {
      value: string
    }
  }
}

interface IconsListMobileDrawerProps {
  itemList: IconItem[]
}

const drawerBleeding = 56 // Example value, adjust as needed

const StyledIconCard = styled(IconCard)`
  z-index: 100;
  position: relative;
  margin-top: -20px;
  max-width: 95%;
  margin-left: auto;
  margin-right: auto;
`
const StyledSwipeableDrawer = styled(SwipeableDrawer)`
  .MuiDrawer-paper {
    max-height: 80%;
    overflow-y: auto;
    padding: ${sizes.baseTriple};
    border-top-right-radius: ${sizes.borderRadius};
    border-top-left-radius: ${sizes.borderRadius};
  }
`
const IconListMobileDrawer: React.FC<IconsListMobileDrawerProps> = ({
  itemList,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [currActive, setCurrActive] = useState<Partial<ActiveItem>>({})
  const pathname = usePathname()
  useEffect(() => {
    if (!pathname) return

    const activeItem = itemList.find((item) => {
      const { link } = transformProperties(item?.properties)
      return (
        link?.links[0]?.url.replace("/pages/", "/").replace(/\/$/, "") ===
        pathname.replace(/\/$/, "")
      )
    })
    const { link, Icon, text, adamTotalID } = transformProperties(
      activeItem?.properties,
    )
    if (activeItem) {
      setCurrActive({
        colorPallette: "turquoise" as colorPalletteIndexType,
        link: getNonUmbracoUrl(link?.links[0]?.url),
        Icon: Icon,
        text: text?.value,
        adamTotalID: adamTotalID?.value ? adamTotalID.value : "all",
      })
    }
  }, [pathname, itemList]) // Add pathname and itemList to dependencies

  const toggleDrawer = (open: boolean) => () => {
    setIsDrawerOpen(open)
  }

  const container =
    typeof window !== "undefined" ? () => window.document.body : undefined

  return (
    <>
      <Mobile>
        <StyledIconCard
          onClick={toggleDrawer(true)}
          variant="elevated"
          colorPallette={currActive?.colorPallette as colorPalletteIndexType}
          ArrowWithLine={false}
          className={"active"}
          href={currActive.link}
        >
          {currActive.Icon && currActive.Icon?.mediaItems && (
            <IconWrapper>
              <Image
                src={getUmbracoMediaUrl(currActive?.Icon?.mediaItems[0]?.url)}
                alt="icon"
                width={48}
                height={48}
              />
            </IconWrapper>
          )}
          <CardTitle variant="body1">{currActive?.text}</CardTitle>
        </StyledIconCard>
        <StyledSwipeableDrawer
          container={container}
          anchor="bottom"
          open={isDrawerOpen}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
          swipeAreaWidth={drawerBleeding}
          disableSwipeToOpen={false}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <IconsLinksList
            itemsList={itemList}
            setActive={(activeItem: Partial<ActiveItem>) =>
              setCurrActive({
                ...activeItem,
                colorPallette: activeItem.colorPallette as
                  | colorPalletteIndexType
                  | undefined,
              })
            }
          />
        </StyledSwipeableDrawer>
      </Mobile>
      <Desktop>
        <IconsLinksList itemsList={itemList} />
      </Desktop>
    </>
  )
}

export default IconListMobileDrawer
